// Live driver tracking — pure CSS/SVG mock without external map dependencies.
// Renders a stylised map of Hamburg's covered districts with 4 animated driver
// pins moving on plausible routes. Creates the "Uber-feel" without GPS data.
import { useEffect, useState } from "react";
import { Truck, MapPin, Activity } from "lucide-react";

const DRIVERS = [
  // Routes now span all of Norddeutschland — each driver runs a plausible daily
  // circuit connecting Hamburg to a neighbouring metropolis.
  { id: "TGG-01", name: "Marwan", svc: "Lieferung Hamburg→Lübeck",  route: [[42, 55], [55, 45], [72, 38], [85, 30], [72, 38], [55, 45], [42, 55]] },
  { id: "TGG-04", name: "Layla",  svc: "Autoteile Hamburg→Bremen",   route: [[42, 55], [30, 60], [18, 65], [12, 62], [18, 65], [30, 60], [42, 55]] },
  { id: "TGG-07", name: "Yusuf",  svc: "Umzug Hamburg→Kiel",         route: [[42, 55], [45, 40], [50, 25], [58, 15], [50, 25], [45, 40], [42, 55]] },
  { id: "TGG-09", name: "Anika",  svc: "E-Scooter Hamburg→Hannover", route: [[42, 55], [40, 68], [42, 80], [45, 90], [42, 80], [40, 68], [42, 55]] },
];

const DISTRICTS = [
  // Layout: Hamburg centre + 4 major cities around it (rough compass positions)
  { name: "HAMBURG",  x: 42, y: 55, primary: true },
  { name: "Lübeck",   x: 68, y: 40 },
  { name: "Kiel",     x: 50, y: 18 },
  { name: "Bremen",   x: 18, y: 64 },
  { name: "Hannover", x: 42, y: 85 },
  { name: "Rostock",  x: 82, y: 25 },
];

const TICK_MS = 80;
const STEPS_PER_LEG = 100;

export default function LiveDriverMap() {
  const [t, setT] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setT((x) => x + 1), TICK_MS);
    return () => clearInterval(id);
  }, []);

  const positionOf = (driver) => {
    const totalSteps = driver.route.length * STEPS_PER_LEG;
    const cur = ((t + driver.id.charCodeAt(driver.id.length - 1) * 13) % totalSteps);
    const legIdx = Math.floor(cur / STEPS_PER_LEG);
    const legPct = (cur % STEPS_PER_LEG) / STEPS_PER_LEG;
    const [x1, y1] = driver.route[legIdx];
    const [x2, y2] = driver.route[(legIdx + 1) % driver.route.length];
    return { x: x1 + (x2 - x1) * legPct, y: y1 + (y2 - y1) * legPct };
  };

  return (
    <section className="py-20 lg:py-28 bg-[#090A0B] text-white relative overflow-hidden" data-testid="live-driver-map">
      <div className="absolute inset-0 opacity-[0.04] [background-image:linear-gradient(rgba(255,255,255,.5)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.5)_1px,transparent_1px)] [background-size:32px_32px]" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center gap-2 text-[#FF5A00] mb-3">
          <Activity size={14} />
          <span className="text-xs font-bold tracking-[0.3em] uppercase">Live · Echtzeit</span>
        </div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mb-3 max-w-2xl">Unsere Fahrer. Jetzt unterwegs in Norddeutschland.</h2>
        <p className="text-neutral-400 max-w-2xl mb-12 leading-relaxed">
          {DRIVERS.length} Talkani-Teams aktuell im Einsatz zwischen Hamburg, Lübeck, Bremen, Kiel und Hannover. Live-Tracking ab Ihrer Buchung verfügbar.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 relative aspect-[4/3] border border-white/10 bg-[#0F1012]">
            {/* Pseudo-map background */}
            <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
              {/* River Elbe — stylised */}
              <path
                d="M 0 65 Q 30 60 50 70 T 100 60"
                stroke="rgba(56,189,248,0.25)"
                strokeWidth="4"
                fill="none"
              />
              {/* City circles — Hamburg = primary (bigger, orange filled) */}
              {DISTRICTS.map((d) => (
                <g key={d.name}>
                  <circle cx={d.x} cy={d.y} r={d.primary ? "3.6" : "2.4"} fill="rgba(255,90,0,0.18)" />
                  <circle cx={d.x} cy={d.y} r={d.primary ? "1.6" : "0.8"} fill="#FF5A00" />
                </g>
              ))}
              {/* Driver routes (faint) */}
              {DRIVERS.map((dr) => (
                <polyline
                  key={dr.id + "-route"}
                  points={dr.route.map(([x, y]) => `${x},${y}`).join(" ")}
                  stroke="rgba(255,255,255,0.08)"
                  strokeWidth="0.5"
                  fill="none"
                  strokeDasharray="1,1.5"
                />
              ))}
              {/* Driver pins — pulse */}
              {DRIVERS.map((dr) => {
                const p = positionOf(dr);
                return (
                  <g key={dr.id}>
                    <circle cx={p.x} cy={p.y} r="2" fill="#FF5A00" opacity="0.4">
                      <animate attributeName="r" values="2;3.5;2" dur="1.8s" repeatCount="indefinite" />
                      <animate attributeName="opacity" values="0.4;0;0.4" dur="1.8s" repeatCount="indefinite" />
                    </circle>
                    <circle cx={p.x} cy={p.y} r="1.4" fill="#FF5A00" stroke="#fff" strokeWidth="0.4" />
                  </g>
                );
              })}
            </svg>
            {/* City labels (HTML for crisp text) — Hamburg emphasised */}
            {DISTRICTS.map((d) => (
              <span
                key={d.name + "-lbl"}
                className={`absolute font-bold tracking-wider uppercase ${
                  d.primary
                    ? "text-[13px] text-[#FF5A00]"
                    : "text-[10px] text-neutral-400"
                }`}
                style={{ left: `${d.x}%`, top: `${d.y + 3}%`, transform: "translateX(-50%)" }}
              >
                {d.name}
              </span>
            ))}
          </div>

          {/* Driver list */}
          <div className="space-y-3">
            {DRIVERS.map((dr) => (
              <div
                key={dr.id}
                className="border border-white/10 bg-white/5 backdrop-blur-sm p-4 flex items-center gap-3"
                data-testid={`driver-${dr.id}`}
              >
                <div className="w-10 h-10 rounded-full bg-[#FF5A00] flex items-center justify-center flex-shrink-0">
                  <Truck size={16} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-black tracking-tight">{dr.name}</span>
                    <span className="text-[10px] font-mono text-neutral-500">{dr.id}</span>
                  </div>
                  <div className="text-xs text-neutral-400 flex items-center gap-1">
                    <MapPin size={10} className="text-[#FF5A00]" />
                    {dr.svc} · unterwegs
                  </div>
                </div>
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
